/**
 * Sync Manager - socket reliability (cursor + catch-up)
 *
 * The server tags every `cache:invalidate` with a monotonic `seq` (the id of a
 * row in winery_event_log) and exposes GET /events?since=<seq>. We track the
 * last seq we applied per winery and, on (re)connect or window focus, ask the
 * server for everything we missed while the socket was down. This makes the
 * best-effort socket channel self-healing.
 *
 * Note on gap detection: `seq` is a GLOBAL serial, so per-winery values are
 * monotonic but NOT contiguous (other wineries consume ids in between).
 * Therefore we do NOT do "seq === lastSeq + 1" gap detection on live events
 * (it would false-positive constantly). Live events are applied as they arrive;
 * the real loss window — a dropped/closed socket — is healed by catchUp() on
 * reconnect and on focus.
 */

import { setTanks } from '../context/tanks/tanksSlice';
import { setOperations } from '../context/operations/operationsSlice';
import { setLots } from '../context/lots/lotsSlice';
import { TANKS, OPERATIONS, LOTS } from '../constants/apis';

const lastSeqByWinery = new Map();

export const getLastSeq = (wineryId) => lastSeqByWinery.get(wineryId) ?? 0;

const bumpSeq = (wineryId, seq) => {
  if (wineryId == null || seq == null || Number.isNaN(Number(seq))) return;
  const current = lastSeqByWinery.get(wineryId) ?? 0;
  if (Number(seq) > current) lastSeqByWinery.set(wineryId, Number(seq));
};

/**
 * Normalize a winery id candidate to a positive integer, or null if it
 * isn't one (missing, empty, non-numeric, non-positive). "12" and 12 are
 * the same id; undefined/null/""/"abc"/0 are not valid ids in this domain.
 */
const toValidWineryId = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * Cross-channel winery isolation gate: true only if both ids are valid and
 * equal. Used before applying any cache-invalidation event (live socket,
 * legacy socket, or cross-tab BroadcastChannel) so an event that belongs to
 * a different winery - or carries no provable winery at all - is never
 * applied to, or attributed to, the currently active winery.
 */
export const isEventForActiveWinery = (eventWineryId, activeWineryId) => {
  const event = toValidWineryId(eventWineryId);
  const active = toValidWineryId(activeWineryId);
  return event !== null && active !== null && event === active;
};

/**
 * Apply a single live `cache:invalidate` event and advance the cursor.
 * @param {Object} event - { affected_entities, seq, ... }
 */
export const applyEvent = async (event, { cacheManager, wineryId }) => {
  if (event?.affected_entities) {
    await cacheManager.processAffectedEntities(event.affected_entities);
  }
  bumpSeq(wineryId, event?.seq);
};

/**
 * Gate + apply a live `cache:invalidate` socket event. Rejects (no-op, no
 * cursor advance, no dispatch) when the event's winery doesn't match the
 * active one, or when either id is missing/invalid - never falls back to
 * assuming the active winery. Calls `onAccepted(event)` only after a real
 * apply, so callers can safely rebroadcast to other tabs from there.
 */
export const applyCacheInvalidateEvent = async (event, { cacheManager, wineryId, onAccepted }) => {
  if (!event?.affected_entities) return { applied: false };
  if (!isEventForActiveWinery(event.winery_id, wineryId)) return { applied: false };

  await applyEvent(event, { cacheManager, wineryId });
  onAccepted?.(event);
  return { applied: true };
};

/**
 * Gate + apply the legacy `CACHE_INVALIDATION` socket event (metadata
 * envelope shape, bypasses the cursor). Same winery-isolation contract as
 * applyCacheInvalidateEvent: a missing/invalid/mismatched winery is ignored,
 * never attributed to the active winery.
 */
export const applyLegacyCacheInvalidationEvent = async (message, { cacheManager, wineryId, onAccepted }) => {
  const affectedEntities = message?.data?.affected_entities;
  if (!affectedEntities) return { applied: false };

  const eventWineryId = message?.winery_id ?? message?.data?.winery_id;
  if (!isEventForActiveWinery(eventWineryId, wineryId)) return { applied: false };

  await cacheManager.processAffectedEntities(affectedEntities);
  onAccepted?.({ affected_entities: affectedEntities, metadata: message?.data?.metadata, winery_id: eventWineryId });
  return { applied: true };
};

/**
 * Gate + apply a cross-tab `CACHE_INVALIDATE` BroadcastChannel payload. The
 * sending tab already gated its own copy, but this tab must not trust that -
 * it repeats the same winery check independently before applying anything.
 * Malformed payloads (not an object, missing fields) are ignored, never thrown.
 */
export const applyBroadcastChannelCacheInvalidate = async (payload, { cacheManager, wineryId }) => {
  const event = payload?.affected_entities ? payload : { affected_entities: payload };
  if (!isEventForActiveWinery(event?.winery_id, wineryId)) return { applied: false };

  await applyEvent(event, { cacheManager, wineryId });
  return { applied: true };
};

/**
 * Strict fetchers for full-resync purposes only. Unlike the legacy fetchers
 * in utils.js (which swallow errors and return [], making a real failure
 * indistinguishable from a genuinely empty snapshot), these rethrow on any
 * HTTP/network error and reject a payload that isn't the expected array, so
 * fullResync can tell "empty" from "broken" and never treat the latter as
 * the former.
 */
const fetchTanksStrict = async (wineryId, axios) => {
  const response = await axios.get(TANKS, { withCredentials: true });
  const data = response?.data;
  if (!Array.isArray(data)) throw new Error('fetchTanksStrict: malformed payload (expected array)');
  return wineryId ? data.filter((tank) => tank.winery_id === wineryId) : data;
};

const fetchOperationsStrict = async (axios) => {
  const response = await axios.get(OPERATIONS, { withCredentials: true, params: { completion_date: '' } });
  const data = response?.data;
  if (!Array.isArray(data)) throw new Error('fetchOperationsStrict: malformed payload (expected array)');
  return data;
};

const fetchLotsStrict = async (wineryId, axios) => {
  const response = await axios.get(LOTS, { withCredentials: true });
  const data = response?.data;
  if (!Array.isArray(data)) throw new Error('fetchLotsStrict: malformed payload (expected array)');
  return wineryId ? data.filter((lot) => lot.winery_id === wineryId) : data;
};

/**
 * A full-resync checkpoint is only trustworthy if latest_seq is a real,
 * non-negative finite number - never undefined/null/NaN/negative. Without
 * that we can't establish a reliable cursor, so we must not perform (or
 * claim to have performed) a full resync at all.
 */
const isValidLatestSeq = (value) => value != null && Number.isFinite(Number(value)) && Number(value) >= 0;

/**
 * Full refetch of the cellar core. Used when the server signals full_resync
 * (the client's cursor is older than the retained event window). Atomic:
 * either all three snapshots are fetched and validated and all three
 * dispatches happen, or none of them do - a failure partway through (even
 * after some snapshots already resolved) must never partially apply state.
 * Throws on any failure; the caller decides what "failure" means for the
 * cursor.
 */
const fullResync = async ({ dispatch, axios, wineryId }) => {
  console.log('🔁 Full resync: refetching tanks/operations/lots');
  const [tanks, operations, lots] = await Promise.all([
    fetchTanksStrict(wineryId, axios),
    fetchOperationsStrict(axios),
    fetchLotsStrict(wineryId, axios),
  ]);
  dispatch(setTanks(tanks));
  dispatch(setOperations(operations));
  dispatch(setLots(lots));
};

/**
 * Catch up after a (re)connect or window focus: replay the events we missed,
 * or full-resync if the server says our cursor is too old. Safe to call often.
 */
export const catchUp = async ({ dispatch, axios, wineryId, cacheManager }) => {
  if (!wineryId) return;
  const since = getLastSeq(wineryId);

  try {
    const { data } = await axios.get('/events', { params: { since } });

    if (data?.full_resync) {
      if (!isValidLatestSeq(data?.latest_seq)) {
        console.error('Catch-up: full_resync requested but latest_seq is missing/invalid; skipping to avoid an unreliable checkpoint.');
        return;
      }
      // Cursor only advances after every snapshot is fetched, validated, and dispatched.
      await fullResync({ dispatch, axios, wineryId });
      bumpSeq(wineryId, data.latest_seq);
      return;
    }

    if (Array.isArray(data?.events) && data.events.length > 0) {
      console.log(`🔄 Catch-up: replaying ${data.events.length} missed event(s)`);
      // Replay in order so deletes/updates apply as they originally happened.
      for (const ev of data.events) {
        if (ev?.affected_entities) {
          await cacheManager.processAffectedEntities(ev.affected_entities);
        }
      }
    }

    if (data?.latest_seq != null) bumpSeq(wineryId, data.latest_seq);
  } catch (err) {
    console.error('Catch-up failed:', err);
  }
};

/**
 * Optimistic-concurrency: build the `client_versions` payload for completing an
 * operation. Only tanks are versioned — tanks.updated_at is the clean
 * "cellar state changed" signal (bumped on real content changes via the server
 * contains trigger, NOT on planning). The values are server-sourced (from the
 * Redux tanks cache), so the server compares timestamp-vs-timestamp (no skew).
 *
 * @param {Object} operation - the operation being completed (has src_tanks/dest_tanks)
 * @param {Array} tanks - current Redux tanks cache (each with id + updated_at)
 * @returns {{tanks: Object}|undefined}
 */
export const buildOperationVersions = (operation, tanks = []) => {
  if (!operation) return undefined;

  const updatedAtById = new Map(tanks.map((t) => [t.id, t.updated_at]));

  const tankIds = new Set();
  for (const t of [...(operation.src_tanks || []), ...(operation.dest_tanks || [])]) {
    const id = t?.tank_id ?? t?.id;
    if (id != null) tankIds.add(id);
  }

  const tanksVersions = {};
  tankIds.forEach((id) => {
    const ts = updatedAtById.get(id);
    if (ts) tanksVersions[id] = ts;
  });

  return Object.keys(tanksVersions).length > 0 ? { tanks: tanksVersions } : undefined;
};

/**
 * Map a 409 `details.conflicts` list ([{type, id}]) into the affected_entities
 * shape the CacheManager consumes, so we can refetch exactly the stale entities.
 */
export const conflictsToAffectedEntities = (conflicts = []) => {
  const typeToKey = { tank: 'tanks', batch: 'batches', drug_batch: 'drug_batches' };
  const out = {};
  for (const c of conflicts) {
    const key = typeToKey[c?.type];
    if (!key || c?.id == null) continue;
    (out[key] = out[key] || []).push({ id: c.id, action: 'update' });
  }
  return out;
};
